import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { couponService } from '../services/couponService';
import ConfirmDialog from '../components/ConfirmDialog';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import NotificationBell from '../components/NotificationBell';

const CouponManagement = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, onConfirm: null, title: '', message: '', type: 'warning' });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState(null);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [stats, setStats] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    description: '',
    type: 'percentage',
    value: '',
    minOrderAmount: '',
    maxDiscount: '',
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: '',
    usageLimit: '',
    userUsageLimit: '1',
    applicableTo: 'all',
    platform: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadCoupons();
  }, [user]);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await couponService.getCoupons();
      if (response.success) {
        setCoupons(response.coupons || []);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      setCreating(true);
      const response = await couponService.createCoupon({
        ...formData,
        value: parseFloat(formData.value),
        minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : 0,
        maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : null,
        usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : null,
        userUsageLimit: parseInt(formData.userUsageLimit) || 1,
        validFrom: new Date(formData.validFrom),
        validUntil: new Date(formData.validUntil),
        platform: formData.applicableTo === 'specific_platform' ? formData.platform : null,
      });

      if (response.success) {
        toast.success('Coupon créé avec succès');
        setShowCreateModal(false);
        setFormData({
          code: '',
          name: '',
          description: '',
          type: 'percentage',
          value: '',
          minOrderAmount: '',
          maxDiscount: '',
          validFrom: new Date().toISOString().split('T')[0],
          validUntil: '',
          usageLimit: '',
          userUsageLimit: '1',
          applicableTo: 'all',
          platform: '',
        });
        loadCoupons();
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Désactiver le coupon',
      message: 'Êtes-vous sûr de vouloir désactiver ce coupon ?',
      type: 'warning',
      onConfirm: async () => {
        setConfirmDialog({ ...confirmDialog, isOpen: false });
        try {
          const response = await couponService.deleteCoupon(couponId);
          if (response.success) {
            toast.success('Coupon désactivé avec succès');
            loadCoupons();
          } else {
            toast.error(`Erreur: ${response.error}`);
          }
        } catch (error) {
          console.error('Erreur:', error);
          toast.error('Une erreur est survenue');
        }
      },
    });
  };

  const handleViewStats = async (couponId) => {
    try {
      const response = await couponService.getCouponStats(couponId);
      if (response.success) {
        setStats(response.stats);
        setShowStatsModal(true);
      } else {
        toast.error(`Erreur: ${response.error}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Une erreur est survenue');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const isCouponValid = (coupon) => {
    const now = new Date();
    return (
      coupon.isActive &&
      now >= new Date(coupon.validFrom) &&
      now <= new Date(coupon.validUntil) &&
      (coupon.usageLimit === null || coupon.usageCount < coupon.usageLimit)
    );
  };

  return (
    <div>
      <Navbar />
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          if (confirmDialog.onConfirm) {
            confirmDialog.onConfirm();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      <div className="container" style={{ maxWidth: '1400px', margin: '40px auto', padding: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
          <h1 style={{ color: '#FF0000', fontSize: '32px', fontWeight: '700', margin: 0 }}>
            🎟️ Gestion des Coupons
          </h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary"
          >
            + Créer un coupon
          </button>
        </div>

        {/* Liste des coupons */}
        {loading ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Chargement...</p>
          </div>
        ) : coupons.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: '#666', marginBottom: '20px' }}>Aucun coupon créé</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn btn-primary"
            >
              Créer le premier coupon
            </button>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: '15px' }}>
            {coupons.map((coupon) => (
              <div key={coupon._id} className="card" style={{ padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '15px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '10px' }}>
                      <h3 style={{ margin: 0, color: '#333', fontSize: '20px' }}>
                        {coupon.code}
                      </h3>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: isCouponValid(coupon) ? '#28a745' : '#6c757d',
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 'bold',
                      }}>
                        {isCouponValid(coupon) ? 'Actif' : 'Inactif'}
                      </span>
                      {!coupon.isActive && (
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: '#dc3545',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}>
                          Désactivé
                        </span>
                      )}
                    </div>
                    <p style={{ margin: '5px 0', color: '#666', fontSize: '16px', fontWeight: 'bold' }}>
                      {coupon.name}
                    </p>
                    {coupon.description && (
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        {coupon.description}
                      </p>
                    )}
                    <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Type:</strong> {coupon.type === 'percentage' ? `${coupon.value}%` : formatCurrency(coupon.value)}
                      </p>
                      {coupon.minOrderAmount > 0 && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                          <strong>Montant min:</strong> {formatCurrency(coupon.minOrderAmount)}
                        </p>
                      )}
                      {coupon.maxDiscount && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                          <strong>Max réduction:</strong> {formatCurrency(coupon.maxDiscount)}
                        </p>
                      )}
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Utilisations:</strong> {coupon.usageCount} / {coupon.usageLimit || '∞'}
                      </p>
                      <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                        <strong>Valide du:</strong> {formatDate(coupon.validFrom)} au {formatDate(coupon.validUntil)}
                      </p>
                      {coupon.applicableTo !== 'all' && (
                        <p style={{ margin: '5px 0', color: '#666', fontSize: '14px' }}>
                          <strong>Applicable à:</strong> {
                            coupon.applicableTo === 'first_order' ? 'Première commande' :
                            coupon.applicableTo === 'specific_platform' ? coupon.platform :
                            'Toutes les commandes'
                          }
                        </p>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
                    <button
                      onClick={() => handleViewStats(coupon._id)}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', whiteSpace: 'nowrap' }}
                    >
                      📊 Statistiques
                    </button>
                    <button
                      onClick={() => handleDeleteCoupon(coupon._id)}
                      className="btn"
                      style={{
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        backgroundColor: '#dc3545',
                        color: '#fff',
                      }}
                    >
                      Désactiver
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal de création */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
            overflowY: 'auto',
          }}
          onClick={() => setShowCreateModal(false)}
        >
          <div
            className="card"
            style={{
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              Créer un nouveau coupon
            </h2>
            <form onSubmit={handleCreateCoupon}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="code">Code du coupon *</label>
                  <input
                    type="text"
                    id="code"
                    value={formData.code}
                    onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                    required
                    placeholder="EXEMPLE2024"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="name">Nom du coupon *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    required
                    placeholder="Promotion spéciale"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows="3"
                  placeholder="Description du coupon..."
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="type">Type de réduction *</label>
                  <select
                    id="type"
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                    required
                  >
                    <option value="percentage">Pourcentage (%)</option>
                    <option value="fixed">Montant fixe ($)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="value">Valeur *</label>
                  <input
                    type="number"
                    id="value"
                    value={formData.value}
                    onChange={(e) => setFormData(prev => ({ ...prev, value: e.target.value }))}
                    required
                    min="0"
                    step={formData.type === 'percentage' ? '1' : '0.01'}
                    placeholder={formData.type === 'percentage' ? '10' : '5.00'}
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="minOrderAmount">Montant minimum de commande</label>
                  <input
                    type="number"
                    id="minOrderAmount"
                    value={formData.minOrderAmount}
                    onChange={(e) => setFormData(prev => ({ ...prev, minOrderAmount: e.target.value }))}
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                  />
                </div>
                {formData.type === 'percentage' && (
                  <div className="form-group">
                    <label htmlFor="maxDiscount">Réduction maximale</label>
                    <input
                      type="number"
                      id="maxDiscount"
                      value={formData.maxDiscount}
                      onChange={(e) => setFormData(prev => ({ ...prev, maxDiscount: e.target.value }))}
                      min="0"
                      step="0.01"
                      placeholder="Aucune limite"
                    />
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="validFrom">Valide à partir du *</label>
                  <input
                    type="date"
                    id="validFrom"
                    value={formData.validFrom}
                    onChange={(e) => setFormData(prev => ({ ...prev, validFrom: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="validUntil">Valide jusqu'au *</label>
                  <input
                    type="date"
                    id="validUntil"
                    value={formData.validUntil}
                    onChange={(e) => setFormData(prev => ({ ...prev, validUntil: e.target.value }))}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div className="form-group">
                  <label htmlFor="usageLimit">Limite d'utilisations totales</label>
                  <input
                    type="number"
                    id="usageLimit"
                    value={formData.usageLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, usageLimit: e.target.value }))}
                    min="0"
                    placeholder="Illimité"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="userUsageLimit">Utilisations par utilisateur *</label>
                  <input
                    type="number"
                    id="userUsageLimit"
                    value={formData.userUsageLimit}
                    onChange={(e) => setFormData(prev => ({ ...prev, userUsageLimit: e.target.value }))}
                    required
                    min="1"
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="applicableTo">Applicable à *</label>
                <select
                  id="applicableTo"
                  value={formData.applicableTo}
                  onChange={(e) => setFormData(prev => ({ ...prev, applicableTo: e.target.value }))}
                  required
                >
                  <option value="all">Toutes les commandes</option>
                  <option value="first_order">Première commande uniquement</option>
                  <option value="specific_platform">Plateforme spécifique</option>
                </select>
              </div>
              {formData.applicableTo === 'specific_platform' && (
                <div className="form-group">
                  <label htmlFor="platform">Plateforme *</label>
                  <select
                    id="platform"
                    value={formData.platform}
                    onChange={(e) => setFormData(prev => ({ ...prev, platform: e.target.value }))}
                    required
                  >
                    <option value="">Sélectionnez une plateforme</option>
                    <option value="Shein">Shein</option>
                    <option value="Amazon">Amazon</option>
                    <option value="Alibaba">Alibaba</option>
                    <option value="AliExpress">AliExpress</option>
                    <option value="Autre">Autre</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={creating}
                >
                  {creating ? 'Création...' : 'Créer le coupon'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de statistiques */}
      {showStatsModal && stats && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={() => setShowStatsModal(false)}
        >
          <div
            className="card"
            style={{ maxWidth: '500px', width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ marginBottom: '20px', color: '#FF0000' }}>
              📊 Statistiques du coupon
            </h2>
            <div style={{ display: 'grid', gap: '15px' }}>
              <div>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Code:</strong> {stats.coupon.code}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Nom:</strong> {stats.coupon.name}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Utilisations:</strong> {stats.coupon.usageCount} / {stats.coupon.usageLimit || '∞'}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Total d'utilisations:</strong> {stats.totalUsage}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Total de réduction:</strong> {formatCurrency(stats.totalDiscount)}
                </p>
                <p style={{ margin: '5px 0', color: '#666' }}>
                  <strong>Utilisateurs uniques:</strong> {stats.uniqueUsers}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowStatsModal(false)}
              className="btn btn-secondary"
              style={{ marginTop: '20px', width: '100%' }}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CouponManagement;

